import React, { createContext, useState, useContext, type ReactNode, useEffect } from 'react';
import { boatInfoService } from '../services/boatInfoService';

// Define the type for boat information
export interface BoatInfo {
    name: string;
    model: string;
    loa: number;
    units: 'metric' | 'imperial';
    // Required measurements
    p?: number;
    e?: number;
    i?: number;
    j?: number;
    // Additional measurements
    bas?: number;
    py?: number;
    ii?: number;
    ji?: number;
    isp?: number;
    jsp?: number;
}

// Context type
interface BoatContextType {
    boatInfo: BoatInfo | null;
    setBoatInfo: (info: BoatInfo) => void;
    clearBoatInfo: () => void;
    updateUnits: (units: 'metric' | 'imperial') => void;
}

// Create the context
const BoatContext = createContext<BoatContextType>({
    boatInfo: null,
    setBoatInfo: () => {},
    clearBoatInfo: () => {},
    updateUnits: () => {}
});

// Provider component
export const BoatInfoProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [boatInfo, setBoatInfo] = useState<BoatInfo | null>(null);

    // Load initial boat info from storage
    useEffect(() => {
        const savedBoatInfo = boatInfoService.getBoatInfo();
        console.log('Initial load from storage:', savedBoatInfo);
        if (savedBoatInfo) {
            setBoatInfo(savedBoatInfo);
        }
    }, []);

    // Debug effect to log boatInfo changes
    useEffect(() => {
        console.log('BoatContext state updated:', boatInfo);
    }, [boatInfo]);

    const updateBoatInfo = (info: BoatInfo) => {
        console.log('Updating boat info:', info);
        setBoatInfo(info);
        boatInfoService.saveBoatInfo(info);
        console.log('Saved to storage, current storage:', localStorage.getItem('boatInfo'));
    };

    const clearBoatInfo = () => {
        setBoatInfo(null);
        boatInfoService.clearBoatInfo();
    };

    const updateUnits = (units: 'metric' | 'imperial') => {
        if (boatInfo) {
            const currentUnits = boatInfo.units;
            
            if (currentUnits !== units) {
                // Helper function to convert and round properly
                const convertValue = (value: number | undefined): number | undefined => {
                    if (!value) return undefined;
                    
                    let convertedValue: number;
                    if (currentUnits === 'metric' && units === 'imperial') {
                        convertedValue = value * 3.28084; // meters to feet
                        return Math.round(convertedValue * 100) / 100; // 2 decimal places for feet
                    } else if (currentUnits === 'imperial' && units === 'metric') {
                        convertedValue = value * 0.3048; // feet to meters
                        return Math.round(convertedValue * 1000) / 1000; // 3 decimal places for meters
                    }
                    return value;
                };
                
                // Convert all measurements
                const convertedBoatInfo: BoatInfo = {
                    ...boatInfo,
                    units,
                    loa: convertValue(boatInfo.loa)!,
                    p: convertValue(boatInfo.p),
                    e: convertValue(boatInfo.e),
                    i: convertValue(boatInfo.i),
                    j: convertValue(boatInfo.j),
                    bas: convertValue(boatInfo.bas),
                    py: convertValue(boatInfo.py),
                    ii: convertValue(boatInfo.ii),
                    ji: convertValue(boatInfo.ji),
                    isp: convertValue(boatInfo.isp),
                    jsp: convertValue(boatInfo.jsp),
                };
                
                setBoatInfo(convertedBoatInfo);
                boatInfoService.saveBoatInfo(convertedBoatInfo);
            }
        }
    };

    return (
        <BoatContext.Provider value={{ 
            boatInfo, 
            setBoatInfo: updateBoatInfo,
            clearBoatInfo,
            updateUnits
        }}>
            {children}
        </BoatContext.Provider>
    );
};

// Custom hook for using the boat context
export const useBoatInfo = () => {
    const context = useContext(BoatContext);
    if (!context) {
        throw new Error('useBoatInfo must be used within a BoatInfoProvider');
    }
    return context;
};